
import { DOCUMENT, Injectable, effect, inject } from '@angular/core';
import { PlatformService } from '../platform/platform.service';
import { StoreConfigurationRepositoryService } from '../store-configuration/store-configuration-repository.service';

export enum Action {
  Login = 'login',
  Register = 'sign_up',
  AddToCart = 'add_to_cart',
  Purchase = 'purchase',
  Checkout = 'begin_checkout',
  PaymentSuccess = 'payment_success',
  PaymentFailure = 'payment_failure',
  PropertyId = 'propertyId',
  // to be done
  ViewContent = 'view_content',
  BidInitiated = 'bid_initiated',
  AIProductDetails = 'ai_products_details',
  AddPaymentInfo = 'add_payment_info',
  Search = 'search'
}
@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  private document = inject<Document>(DOCUMENT);
  private platformService = inject(PlatformService);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);


  // Signal-based store configuration
  private storeConfigurations = this.storeConfigurationRepositoryService.storeConfigurations;
  private google_ads = this.storeConfigurations()?.google_ads || null;
  private propertyId = '';
  private hasWarnedAboutGtag = false; // Track if we've already warned about missing gtag
  private gtagCheckInterval: any = null; // Track interval for checking gtag availability

  constructor() {
    // Initialize propertyId from signal
    this.propertyId = this.storeConfigurations()?.google_ads?.propertyId ?? '';

    // Update propertyId when store configurations change
    effect(() => {
      const configs = this.storeConfigurations();
      if (configs?.google_ads?.propertyId) {
        this.propertyId = configs.google_ads.propertyId;
      }
    });
  }

  private checkGtagAvailability(): boolean {
    return typeof (window as any).gtag === 'function';
  }

  private waitForGtagAndRetry(callback: () => void, maxRetries = 10): void {
    if (this.checkGtagAvailability()) {
      callback();
      return;
    }

    if (maxRetries <= 0) {
      if (!this.hasWarnedAboutGtag) {
        this.hasWarnedAboutGtag = true;
      }
      return;
    }

    // Clear any existing interval
    if (this.gtagCheckInterval) {
      clearTimeout(this.gtagCheckInterval);
    }

    // Retry after a short delay
    this.gtagCheckInterval = setTimeout(() => {
      this.waitForGtagAndRetry(callback, maxRetries - 1);
    }, 1000); // Wait 1 second before retrying
  }

  private prepareScript({
    action,
    value = 0,
    category = 'ecommerce',
    send_to = this.propertyId,
    items = [],
    content_name = '',
    transaction_id = ''
  }: {
    action: any;
    value?: number;
    category?: string;
    send_to?: string;
    items?: any[];
    content_name?: string;
    transaction_id?: string;
  }) {

    let script = `gtag('event', '${action}', {
      'event_category': '${category}',
      'send_to': '${send_to}',
      'value': ${value},
    });`;

    if (action === Action.Purchase) {
      script = `gtag('event', '${action}', {
        'event_category': '${category}',
        'send_to': '${send_to}',
        'value': ${value},
        'transaction_id': '${transaction_id}'
      });`;
      return script;
    }

    if (action === Action.Search) {
      script = `gtag('event', '${action}', {
        'event_category': '${category}',
        'send_to': '${send_to}',
        'value': ${value},
        'search_term': '${content_name}',
        'transaction_id': '${'search_' + content_name + Date.now()}'
      });`;
      return script;
    }

    if (Array.isArray(items) && items?.length) {
      script =
        `gtag('event', '${action}', {
        'event_category': '${category}',
        'send_to': '${send_to}',
        'value': ${value},
        'items': ${JSON.stringify(items)}
      });`;
    }

    if (content_name) {
      script = `gtag('event', '${action}', {
        'event_category': '${category}',
        'send_to': '${send_to}',
        'transaction_id': '${transaction_id || 'view_' + Date.now()}',
        'value': ${value},
        'items':[{
          'item_name': '${content_name}'
        }]
      });`;
    }

    return script;
  }

  injectScript({
    action,
    value = 0,
    category = 'ecommerce',
    items = [],
    content_name = '',
    transaction_id = ''
  }: { action: Action, value?: number, category?: string, send_to?: string, items?: any[], content_name?: string, transaction_id?: string }) {

    const scriptId = action;

    if (!this.platformService.isBrowser()) {
      return;
    }

    // Use reactive approach to wait for gtag and retry
    this.waitForGtagAndRetry(() => {
      let scriptElement = this.document.getElementById(scriptId) as HTMLScriptElement;
      if (scriptElement) {
        this.removeScript(scriptId);
      }

      scriptElement = this.document.createElement('script');
      scriptElement.id = scriptId;

      // Get current store configurations from signal
      const currentConfigs = this.storeConfigurations();
      const currentGoogleAds = currentConfigs?.google_ads || null;

      try {
        scriptElement.innerHTML = this.prepareScript({
          action,
          value: currentGoogleAds?.[action + '_value'] || value || 1,
          category,
          send_to: currentGoogleAds?.[action] || this.propertyId,
          items,
          content_name,
          transaction_id
        });
        this.document.head.appendChild(scriptElement);
      } catch (error) {
      }
    });
  }

  injectEmailVariableScript(email: string) {
    if (!this.platformService.isBrowser()) {
      return;
    }

    // Check if gtag is available before injecting script
    if (typeof (window as any).gtag !== 'function') {
      if (!this.hasWarnedAboutGtag) {
        this.hasWarnedAboutGtag = true;
      }
      return;
    }

    try {
      const scriptElement = this.document.createElement('script');
      const innerHtml = `gtag('set', 'user_data', {'email': '${email}'});`
      scriptElement.innerHTML = innerHtml;
      this.document.head.appendChild(scriptElement);
    } catch (error) {
    }
  }

  injectPurchaseConversionScript({
    orderId,
    value = 0
  }: {
    orderId: any;
    value?: number;
  }) {
    if (!this.platformService.isBrowser()) {
      return;
    }

    // Check if gtag is available before injecting script
    if (typeof (window as any).gtag !== 'function') {
      if (!this.hasWarnedAboutGtag) {
        this.hasWarnedAboutGtag = true;
      }
      return;
    }

    try {
      const script = `gtag('event', 'conversion', {
        'send_to': '${this.propertyId}',
        'value': ${value},
        'currency': 'INR',
        'transaction_id': '${orderId}'
      });`
      const scriptElement = this.document.createElement('script');
      scriptElement.innerHTML = script;
      this.document.head.appendChild(scriptElement);
    } catch (error) {
    }
  }

  removeScript(scriptId: string) {
    const scriptElement = this.document.getElementById(scriptId) as HTMLScriptElement;
    if (scriptElement) {
      scriptElement.remove();
    }
  }
}
